import { useMemo } from "react";
import { getEnvironmentRestUrl } from "../config/env";

const getRestUrlBase = () => getEnvironmentRestUrl();

const responseReturnByContentType = (
  response: Response,
  responseContentType?: string
) => {
  // If response has no content, return null/void
  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return null;
  }

  const contentType =
    response.headers.get("content-type")?.split(";")[0] ||
    responseContentType ||
    "text/plain";

  // cases for many of the common MIME types. If not managed here
  // use can use handleResponseReturn callback to manage the response
  switch (contentType) {
    case "application/json":
    case String(contentType.match(/application\/.*\+json/)?.[0]): // catch all for application/*+json
      return response.json();
    case "text/html":
    case "text/plain":
    case "application/xml":
    case "text/xml":
    case "application/javascript":
    case "application/x-www-form-urlencoded":
      return response.text();
    case "application/octet-stream":
    case "application/pdf":
    case "application/zip":
    case "image/jpeg":
    case "image/png":
    case "image/gif":
      return response.blob();
    default:
      return response;
  }
};

export interface UseRequestOptions {
  contentType?: string;
  withThrow?: boolean;
  errorMessage?: string;
  handleResponseReturn?: (response: Response, contentType?: string) => unknown;
}

const defaultHeaders = {
  Accept: "*/*",
  "Accept-Encoding": "gzip, deflate, br, zstd",
  "Accept-Language": "en-US,en;q=0.9",
  "Sec-Fetch-Dest": "empty",
  "Sec-Fetch-Mode": "cors",
  "Sec-Fetch-Site": "same-site",
};

export const useRequest = ({
  contentType = "application/json",
  withThrow = true,
  errorMessage,
  handleResponseReturn,
}: UseRequestOptions = {}) => {
  const headers = useMemo(
    () => ({
      ...defaultHeaders,
    }),
    []
  );

  const headersWithContentType = useMemo(
    () => ({
      ...headers,
      "Content-Type": contentType,
    }),
    [headers, contentType]
  );

  return {
    get: async <T>(
      endpoint: string,
      queryParams?: Record<string, string | undefined>,
      additionalHeaders?: Record<string, string>
    ): Promise<T> => {
      try {
        const url = new URL(`${getRestUrlBase()}/${endpoint}`);
        if (queryParams) {
          Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== undefined) url.searchParams.set(key, value);
          });
        }
        const response = await fetch(url.toString(), {
          headers: {
            ...headersWithContentType,
            ...additionalHeaders,
          },
        });

        if (!response.ok) {
          if (withThrow) throw new Error("Failed to fetch data");
        }

        if (typeof handleResponseReturn === "function") {
          return handleResponseReturn(response, contentType) as T;
        }
        return responseReturnByContentType(response, contentType) as T;
      } catch (error) {
        if (withThrow) throw error;
        return {} as T;
      }
    },
    post: async <T>(
      url: string,
      body?: BodyInit | null,
      additionalHeaders?: Record<string, string>
    ): Promise<T> => {
      try {
        const response = await fetch(`${getRestUrlBase()}/${url}`, {
          method: "POST",
          headers: {
            ...headersWithContentType,
            ...additionalHeaders,
          },
          body,
        });

        if (!response.ok) {
          if (withThrow) throw new Error("Failed to post data");
        }

        if (typeof handleResponseReturn === "function") {
          return handleResponseReturn(response, contentType) as T;
        }
        return responseReturnByContentType(response, contentType) as T;
      } catch (error) {
        if (withThrow) throw error;
        return {} as T;
      }
    },
    put: async <T>(
      url: string,
      body: BodyInit | null,
      additionalHeaders?: Record<string, string>
    ): Promise<T> => {
      try {
        const response = await fetch(`${getRestUrlBase()}/${url}`, {
          method: "PUT",
          headers: {
            ...headersWithContentType,
            ...additionalHeaders,
          },
          body,
        });

        if (!response.ok) {
          if (withThrow) throw new Error("Failed to put data");
        }

        if (typeof handleResponseReturn === "function") {
          return handleResponseReturn(response, contentType) as T;
        }
        return responseReturnByContentType(response, contentType) as T;
      } catch (error) {
        if (withThrow) throw error;
        return {} as T;
      }
    },
    del: async <T>(
      url: string,
      additionalHeaders?: Record<string, string>
    ): Promise<T> => {
      try {
        const response = await fetch(`${getRestUrlBase()}/${url}`, {
          method: "DELETE",
          headers: {
            ...headersWithContentType,
            ...additionalHeaders,
          },
        });

        if (!response.ok) {
          if (withThrow) throw new Error("Failed to delete data");
        }

        if (typeof handleResponseReturn === "function") {
          return handleResponseReturn(response, contentType) as T;
        }
        return responseReturnByContentType(response, contentType) as T;
      } catch (error) {
        if (withThrow) throw error;
        return {} as T;
      }
    },
    isReady: useMemo(() => true, []),
  };
};
